import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { CustomerServiceOutlined, PhoneOutlined, SoundOutlined } from '@ant-design/icons';
import { ChatIcon, PhoneFilledIcon } from 'assets';
import { SingleAgentMapSingleStatusesType } from './AccessManageDtv';
import { useAgentMapStore } from 'hooks/agent-maps/useAgentMapStore';
import { useDispatch, useSelector } from 'react-redux';
import { formatTime } from '../../utils/agent-map/statusUtils';
import { RootState } from 'store';
import { useProfile } from 'hooks';
import { AgentMapSeatDto } from 'types';

// Alert threshold from environment variables
const ALERT_THRESHOLD_MINUTES = parseInt(process.env.REACT_APP_AGENT_STATUS_ALERT_THRESHOLD_MINUTES || '5', 10);
const ALERT_THRESHOLD_SECONDS = ALERT_THRESHOLD_MINUTES * 60;

interface SeatProps {
  seat: any;
  seconds: number;
  showAlert: boolean;
  showCounters: boolean;
  callCount: number;
  chatCount: number;
  backgroundColor: string;
  textColor: string;
  iconColor: string;
}

// Define interface for SeatTimer props
interface SeatTimerProps {
  seconds: number;
  isMenuOpen: boolean;
}

/**
 * Component cho Seat để tránh render lại không cần thiết
 */
const SeatTimer = React.memo(({ seconds, isMenuOpen }: SeatTimerProps) => {
  const [frozenTime, setFrozenTime] = useState(formatTime(seconds));

  // Only update the frozen time when the menu is closed
  useEffect(() => {
    if (!isMenuOpen) {
      setFrozenTime(formatTime(seconds));
    }
  }, [seconds, isMenuOpen]);

  return <p className='text-xs'>{frozenTime}</p>;
});

// Create a stable object OUTSIDE of React component lifecycle to persist across renders
const globalMenuState = {
  visibleMenus: new Map<string, boolean>(),
  frozenTimers: new Map<string, number>(),
  lastInteraction: Date.now()
};

// Format time utility function with hours:minutes:seconds format
const formatTimeDisplay = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Modify the Seat component to use the global state for menu visibility
const Seat = memo<SeatProps>(
  ({ seat, seconds, showAlert, showCounters, callCount = 0, chatCount = 0, backgroundColor, textColor, iconColor }) => {
    // Create a unique key for this seat
    const seatKey = `${seat.agentMapSeatId || 0}-${seat.extensionId || ''}`;
    const isOnCall = seat.statusName === 'ONCALL';

    // Use reference to track menu visibility to avoid re-renders
    const menuVisibleRef = useRef(globalMenuState.visibleMenus.get(seatKey) || false);

    // Use state only for forcing UI updates, not for tracking actual visibility
    const [forceUpdate, setForceUpdate] = useState(0);

    // Handle context menu
    const handleContextMenu = (e: React.MouseEvent): void => {
      if (!isOnCall) return;

      e.preventDefault();
      e.stopPropagation();

      // Store the current seconds when menu opens (freeze display only)
      globalMenuState.frozenTimers.set(seatKey, seconds);
      globalMenuState.visibleMenus.set(seatKey, true);
      globalMenuState.lastInteraction = Date.now();

      // Update the ref and force a render
      menuVisibleRef.current = true;
      setForceUpdate((prev) => prev + 1);
    };

    // Handle dropdown visibility change
    const handleVisibleChange = (visible: boolean): void => {
      if (!isOnCall) return;

      // Update the global state
      globalMenuState.visibleMenus.set(seatKey, visible);

      if (visible) {
        // Store the current seconds when menu opens (freeze display only)
        globalMenuState.frozenTimers.set(seatKey, seconds);
      } else {
        // Clean up when menu closes
        globalMenuState.frozenTimers.delete(seatKey);
      }

      // Update the ref and force a render
      menuVisibleRef.current = visible;
      setForceUpdate((prev) => prev + 1);
    };

    // Handle menu item click
    const handleMenuClick = ({ key }: { key: string }): void => {
      // console.log(`Action: ${key} for extension ${seat.extensionId}`);

      // Clean up global state
      globalMenuState.visibleMenus.set(seatKey, false);
      globalMenuState.frozenTimers.delete(seatKey);

      // Update ref and force render
      menuVisibleRef.current = false;
      setForceUpdate((prev) => prev + 1);
    };

    // Format the timer display - ALWAYS use current seconds from props, only freeze DISPLAY when menu is open
    const displayedTimer = useMemo(() => {
      // If menu is open, use frozen timer value for DISPLAY only
      if (menuVisibleRef.current && globalMenuState.frozenTimers.has(seatKey)) {
        const frozenSeconds = globalMenuState.frozenTimers.get(seatKey) || seconds;
        return formatTimeDisplay(frozenSeconds);
      }

      // Otherwise use the actual seconds prop (which continues counting from parent)
      return formatTimeDisplay(seconds);
    }, [seatKey, seconds, forceUpdate]); // Include forceUpdate to update when menu state changes

    // Seat content - keep your existing UI
    const seatContent = (
      <div
        className='relative flex h-[88px] w-[88px] flex-col rounded-md border border-[#B2B9C4] pl-2 pt-2 shadow-md'
        style={{
          backgroundColor,
          color: textColor,
          transition: 'background-color 0.2s ease-in-out',
          cursor: isOnCall ? 'context-menu' : 'default'
        }}
        onContextMenu={handleContextMenu}
      >
        {/* Alert indicator */}
        {showAlert && (
          <div
            className='absolute right-0 top-0 h-4 w-4 rounded-full bg-red-600'
            style={{ boxShadow: '0 0 3px rgba(0,0,0,0.3)', border: '1.5px solid white', zIndex: 10 }}
          />
        )}

        {!showCounters ? (
          // Standard layout with frozen timer
          <>
            <div className='flex text-xs'>
              {seat.canCall !== false && (
                <div className='flex pb-1 pr-1'>
                  <PhoneFilledIcon height={16} width={16} style={{ fill: iconColor }} />
                </div>
              )}
              {seat.canChat !== false && (
                <div className='flex items-center'>
                  <ChatIcon height={16} width={16} fill={iconColor} />
                </div>
              )}
            </div>
            <div className='flex flex-col'>
              <p className='text-xs'>Ext.{seat.extensionId}</p>
              <p className='text-xs'>{seat.shortName}</p>
              {seat.extensionId && <p className='text-xs'>{displayedTimer}</p>}
            </div>
          </>
        ) : (
          // Counter layout
          <>
            <div className='flex flex-col'>
              <p className='text-xs'>Ext.{seat.extensionId}</p>
              <p className='text-xs'>{seat.shortName}</p>
            </div>
            <div className='mb-2 mt-auto flex flex-col gap-1'>
              {seat.canCall !== false && (
                <div className='flex items-center'>
                  <PhoneFilledIcon height={16} width={16} style={{ fill: iconColor, marginRight: 2 }} />
                  <span className='text-xs'>{callCount}</span>
                </div>
              )}
              {seat.canChat !== false && (
                <div className='flex items-center'>
                  <ChatIcon height={16} width={16} fill={iconColor} style={{ marginRight: 2 }} />
                  <span className='text-xs'>{chatCount}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );

    // Only wrap with Dropdown for ONCALL seats
    if (!isOnCall) {
      return seatContent;
    }

    return (
      <Dropdown
        overlay={
          <Menu onClick={handleMenuClick}>
            <Menu.Item key='listen' icon={<CustomerServiceOutlined />}>
              Nghe lén
            </Menu.Item>
            <Menu.Item key='barge' icon={<PhoneOutlined />}>
              Đàm thoại tay ba
            </Menu.Item>
            <Menu.Item key='whisper' icon={<SoundOutlined />}>
              Nghe xen
            </Menu.Item>
          </Menu>
        }
        trigger={['contextMenu']}
        visible={menuVisibleRef.current}
        onVisibleChange={handleVisibleChange}
        destroyPopupOnHide={false}
        getPopupContainer={(node) => node.parentElement || document.body}
      >
        {seatContent}
      </Dropdown>
    );
  },
  (prevProps: SeatProps, nextProps: SeatProps) => {
    // CRITICAL: Only prevent re-renders when menu is open to keep menu stable
    if (nextProps.seat.statusName === 'ONCALL') {
      const seatKey = `${nextProps.seat.agentMapSeatId || 0}-${nextProps.seat.extensionId || ''}`;
      if (globalMenuState.visibleMenus.get(seatKey)) {
        // When menu is open, still allow timer updates but prevent other re-renders
        // Only skip re-render if the timer difference is small (same second)
        const timeDiff = Math.abs(nextProps.seconds - prevProps.seconds);
        if (timeDiff < 1) {
          return true; // Skip re-render for sub-second changes
        }
        // Allow re-render for second-level timer updates even when menu is open
        return false;
      }
    }

    // For non-menu or non-ONCALL seats, allow normal re-renders
    return false;
  }
);

/**
 * Component hiển thị nhiều bản đồ agent cùng lúc
 */
const MultipleAgentMap: React.FC<{
  singleAgentMapAgentStatuses: SingleAgentMapSingleStatusesType[];
  showCounters: boolean;
}> = ({ singleAgentMapAgentStatuses, showCounters }) => {
  const dispatch = useDispatch();
  const { userPermissionIds } = useProfile();

  // Use Redux store for state
  const { mapData: multipleAgentMaps, getSeatCounters, getSeatSeconds, selectedMaps, selectMaps } = useAgentMapStore();

  // Add a state to force re-render
  const [forceRender, setForceRender] = useState(0);

  // Force a re-render when multipleAgentMaps changes
  useEffect(() => {
    setForceRender((prev) => prev + 1);
  }, [multipleAgentMaps]);

  // Add a useSelector to directly listen for lastSocketUpdateTime changes
  const lastUpdateTime = useSelector((state: RootState) => state.agentMap.lastSocketUpdateTime);

  // Force a re-render when lastSocketUpdateTime changes
  useEffect(() => {
    setForceRender((prev) => prev + 1);
  }, [lastUpdateTime]);

  // Socket management - only initialize if user has permission
  // useEffect(() => {
  //     const isAllowViewAgentMap = checkPermissions(userPermissionIds, PERMISSION.READ_AGENT_MAP);

  //     if (isAllowViewAgentMap) {
  //         console.log('🔄 MultipleAgentMap: Component mounted, initializing socket');
  //         socketService.reinitialize();
  //     } else {
  //         console.log('⚠️ User lacks agent map permissions - socket not initialized');
  //     }

  //     return () => {
  //         console.log('🔄 Clearing agent map state on unmount');
  //         dispatch(resetData());
  //     };
  // }, [dispatch, userPermissionIds]);

  // // Handle window focus
  // useEffect(() => {
  //     const handleFocus = () => {
  //         console.log('🔄 Window regained focus, reinitializing socket and refreshing data');
  //         socketService.reinitialize();
  //         if (typeof selectMaps === 'function' && selectedMaps && selectedMaps.length > 0) {
  //             selectMaps(selectedMaps, true);
  //         }
  //     };

  //     window.addEventListener('focus', handleFocus);
  //     return () => {
  //         window.removeEventListener('focus', handleFocus);
  //     };
  // }, [selectedMaps, selectMaps]);

  // Timer state for updating
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Debug log to check what maps we have
  // useEffect(() => {
  //     console.log('🔍 MultipleAgentMap rendering with maps:',
  //         multipleAgentMaps.map(map => `${map.mapId} (${map.seats.length} seats)`));
  // }, [multipleAgentMaps]);

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      <div className='flex-1 overflow-auto p-4' style={{ height: 'calc(100% - 20px)' }}>
        {multipleAgentMaps.map((agentMap) => (
          <div key={`${agentMap.mapId}-${forceRender}`} className='mb-6'>
            {/* Display special name for online map, use name for regular maps */}
            <h2 className='top-0 z-10 mb-4 py-2 text-lg font-bold'>
              {agentMap.mapId === -999 ? 'Bản đồ Online' : agentMap.name || `Bản đồ ${agentMap.mapId}`}
            </h2>

            {/* Display seat count */}
            {/* <p className="text-sm mb-2 text-gray-500">
                            Có {agentMap.seats.filter(seat => !isLoggedOff(seat.statusName, seat.extensionId)).length} ghế đang hoạt động
                        </p> */}

            <div className='flex flex-wrap gap-2'>
              {agentMap.seats
                .filter((seat) => seat.extensionId) // ✅ ADD: Only show seats with extensionId
                .map((seat: AgentMapSeatDto) => {
                  const agentMapSeatId = seat.agentMapSeatId || 0;
                  const seconds = getSeatSeconds(agentMapSeatId);
                  const counters = getSeatCounters(agentMapSeatId);

                  // Get status colors based on seat status
                  const getStatusColors = (statusName: string | undefined) => {
                    switch (statusName) {
                      case 'UNPAUSED':
                        return { backgroundColor: '#06A77D', textColor: '#FFFFFF', iconColor: '#FFFFFF' };
                      case 'PAUSED':
                        return { backgroundColor: '#FF9500', textColor: '#FFFFFF', iconColor: '#FFFFFF' };
                      case 'ONCALL':
                        return { backgroundColor: '#D1131D', textColor: '#FFFFFF', iconColor: '#FFFFFF' };
                      case 'RINGING':
                        return { backgroundColor: '#AF52DE', textColor: '#FFFFFF', iconColor: '#FFFFFF' };
                      case 'CREATING_TICKET':
                        return { backgroundColor: '#1564E8', textColor: '#FFFFFF', iconColor: '#FFFFFF' };
                      case 'LOGIN':
                      case 'FAILED_LOGIN':
                        return { backgroundColor: '#E8F4FC', textColor: '#000000', iconColor: '#000000' };
                      default:
                        return { backgroundColor: '#EBEBED', textColor: '#000000', iconColor: '#000000' };
                    }
                  };

                  const statusColors = getStatusColors(seat.statusName);
                  const backgroundColor = seat.fillColor || statusColors.backgroundColor;
                  const textColor = seat.textColor || statusColors.textColor;
                  const iconColor = seat.iconColor || statusColors.iconColor;

                  const showAlert = seconds > ALERT_THRESHOLD_MINUTES * 60;

                  return (
                    <Seat
                      key={`${agentMapSeatId}-${seat.statusName}`} // Stable key without timer-related values
                      seat={seat}
                      seconds={seconds}
                      showAlert={showAlert}
                      showCounters={showCounters}
                      callCount={counters.call}
                      chatCount={counters.chat}
                      backgroundColor={backgroundColor}
                      textColor={textColor}
                      iconColor={iconColor}
                    />
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination indicator */}
      {multipleAgentMaps.length > 1 && (
        <div className='py-1 text-center text-xs text-gray-500'>Kéo xuống để xem thêm tầng</div>
      )}

      {/* Show this if no maps or only empty maps */}
      {(multipleAgentMaps.length === 0 || multipleAgentMaps.every((map) => map.seats.length === 0)) && (
        <div className='flex h-full items-center justify-center'>
          <p className='text-gray-500'>Không có dữ liệu bản đồ nào được chọn</p>
        </div>
      )}
    </div>
  );
};

export default memo(MultipleAgentMap);